import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  notification,
  Popconfirm,
  Row,
  Select,
  Space,
} from "antd";
import { withRouter } from "../../../../utils/tms-with-router/tms-with-router";
import tmsRouteMap from "../../../../constants/tms-route.map";
import TmsCounterScanPermissionService from "../../../../services/tms-counter-scan-permission.service";
import TmsSevasService from "../../../../services/tms-sevas.service";
import TmsDarshanamService from "../../../../services/tms-darshanam/tms-darshanam.service";
import TmsDonationService from "../../../../services/tms-donation.service";
import TmsAccommodationService from "../../../../services/tms-accommodation.service";
import TmsPrasadamService from "../../../../services/tms-prasadam.service";
import catchErrorFunction from "../../../../utils/tms-catch-error/tms-catch-error";
import TmsTable from "../../../../components/tms-customs/tms-table";

const FETCHABLE_TYPES = {
  seva: () => TmsSevasService.getSevas(1, 200),
  darshanam: () => TmsDarshanamService.getDarshanam(1, 200),
  donation: () => TmsDonationService.getDonations(1, 200),
  accommodation: () => TmsAccommodationService.getAllBlocks(1, 200),
  prasadam: () => TmsPrasadamService.getPrasadam(1, 200),
};

const titleCase = (str) =>
  str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const TmsSmCounterScanPermissions = ({ router }) => {
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [productCache, setProductCache] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editProductIds, setEditProductIds] = useState([]);
  const [addProductType, setAddProductType] = useState(null);
  const [addProductIds, setAddProductIds] = useState([]);
  const [addProductList, setAddProductList] = useState([]);
  const [addProductListLoading, setAddProductListLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const params = new URLSearchParams(router?.location?.search);
  const counterId = params.get("counterID");
  const counterName = params.get("counterName");

  const templeDetails = JSON.parse(localStorage.getItem("templeDetails"));
  const enabledProducts =
    templeDetails?.configuration?.scanPermissionEnabledProducts ?? [];

  useEffect(() => {
    if (!counterId || counterId === "") {
      router.navigate(tmsRouteMap.serviceManagement_counter_management);
      return;
    }
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const resp =
        await TmsCounterScanPermissionService.getPermissions(counterId);
      const items = resp?.items ?? [];
      setPermissions(items);
      // Fetch product lists for types that have non-empty productIds
      const typesToFetch = [
        ...new Set(
          items
            .filter(
              (p) =>
                p.productIds?.length > 0 && FETCHABLE_TYPES[p.productType]
            )
            .map((p) => p.productType)
        ),
      ];
      const newCache = { ...productCache };
      await Promise.all(
        typesToFetch.map(async (type) => {
          if (!newCache[type]) {
            try {
              const data = await FETCHABLE_TYPES[type]();
              newCache[type] = data?.items ?? [];
            } catch {
              newCache[type] = [];
            }
          }
        })
      );
      setProductCache(newCache);
    } catch (error) {
      catchErrorFunction(
        error,
        "Token Expired, redirecting to login page",
        router
      );
    }
    setLoading(false);
  };

  const resolveProductNames = (productType, productIds) => {
    if (!productIds || productIds.length === 0) return "All Products";
    const products = productCache[productType] ?? [];
    return productIds
      .map((id) => {
        const found = products.find((p) => p.id === id);
        return found?.name ?? `ID: ${id}`;
      })
      .join(", ");
  };

  const fetchProductListForType = async (type) => {
    if (!FETCHABLE_TYPES[type]) {
      return [];
    }
    if (productCache[type]) {
      return productCache[type];
    }
    try {
      const data = await FETCHABLE_TYPES[type]();
      const items = data?.items ?? [];
      setProductCache((prev) => ({ ...prev, [type]: items }));
      return items;
    } catch {
      return [];
    }
  };

  // --- Add form handlers ---
  const handleAddTypeChange = async (type) => {
    setAddProductType(type);
    setAddProductIds([]);
    if (FETCHABLE_TYPES[type]) {
      setAddProductListLoading(true);
      const list = await fetchProductListForType(type);
      setAddProductList(list);
      setAddProductListLoading(false);
    } else {
      setAddProductList([]);
    }
  };

  const handleAdd = async () => {
    if (!addProductType) {
      notification.error({ message: "Please select a product type" });
      return;
    }
    setAddLoading(true);
    try {
      await TmsCounterScanPermissionService.createPermission(
        counterId,
        addProductType,
        addProductIds
      );
      notification.success({ message: "Permission added successfully" });
      setAddProductType(null);
      setAddProductIds([]);
      setAddProductList([]);
      await loadPermissions();
    } catch (error) {
      catchErrorFunction(error, "Failed to add permission", router);
    }
    setAddLoading(false);
  };

  // --- Inline edit handlers ---
  const handleEditStart = async (record) => {
    setEditingId(record.id);
    setEditProductIds(record.productIds ?? []);
    if (
      FETCHABLE_TYPES[record.productType] &&
      !productCache[record.productType]
    ) {
      await fetchProductListForType(record.productType);
    }
  };

  const handleEditSave = async (record) => {
    try {
      await TmsCounterScanPermissionService.updatePermission(
        record.id,
        editProductIds
      );
      notification.success({ message: "Permission updated successfully" });
      setEditingId(null);
      await loadPermissions();
    } catch (error) {
      catchErrorFunction(error, "Failed to update permission", router);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditProductIds([]);
  };

  // --- Delete handler ---
  const handleDelete = async (id) => {
    try {
      await TmsCounterScanPermissionService.deletePermission(id);
      notification.success({ message: "Permission deleted successfully" });
      await loadPermissions();
    } catch (error) {
      catchErrorFunction(error, "Failed to delete permission", router);
    }
  };

  // Product types available for adding (not already assigned)
  const assignedTypes = permissions.map((p) => p.productType);
  const availableTypes = enabledProducts.filter(
    (t) => !assignedTypes.includes(t)
  );

  const columns = [
    {
      title: "Product Type",
      dataIndex: "productType",
      key: "productType",
      render: (text) => titleCase(text),
    },
    {
      title: "Products",
      dataIndex: "productIds",
      key: "productIds",
      render: (productIds, record) => {
        if (editingId === record.id) {
          const isFetchable = !!FETCHABLE_TYPES[record.productType];
          const list = productCache[record.productType] ?? [];
          return (
            <Select
              mode="multiple"
              maxTagCount="responsive"
              allowClear
              showSearch
              style={{ width: "100%", minWidth: "200px" }}
              placeholder={
                isFetchable
                  ? "Select products (empty = all)"
                  : "All Products"
              }
              value={editProductIds}
              onChange={(val) => setEditProductIds(val)}
              disabled={!isFetchable}
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {list.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          );
        }
        return resolveProductNames(record.productType, productIds);
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        if (editingId === record.id) {
          return (
            <Space size="middle">
              <a onClick={() => handleEditSave(record)}>Save</a>
              <a onClick={handleEditCancel}>Cancel</a>
            </Space>
          );
        }
        return (
          <Space size="middle">
            {FETCHABLE_TYPES[record.productType] && (
              <a onClick={() => handleEditStart(record)}>Edit</a>
            )}
            <Popconfirm
              title="Are you sure to delete this permission?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <a>Delete</a>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Row
        style={{ width: "100%", marginBottom: "15px" }}
        justify="space-between"
        align="middle"
      >
        <Col>
          <h3 style={{ margin: 0 }}>Scan Permissions - {counterName}</h3>
        </Col>
        <Col>
          <Button
            onClick={() =>
              router.navigate(
                tmsRouteMap.serviceManagement_counter_management
              )
            }
          >
            Back
          </Button>
        </Col>
      </Row>

      <Row style={{ width: "100%" }}>
        <TmsTable
          dataSource={permissions.map((p) => ({ ...p, key: p.id }))}
          loading={loading}
          columns={columns}
          pagination={false}
        />
      </Row>

      {/* Add new permission form */}
      <Row
        style={{ width: "100%", marginTop: "20px" }}
        gutter={[10, 10]}
        align="middle"
      >
        <Col span={6}>
          <Select
            style={{ width: "100%" }}
            placeholder="Select product type"
            value={addProductType}
            onChange={handleAddTypeChange}
            allowClear
            onClear={() => {
              setAddProductType(null);
              setAddProductIds([]);
              setAddProductList([]);
            }}
          >
            {availableTypes.map((type) => (
              <Select.Option key={type} value={type}>
                {titleCase(type)}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={12}>
          <Select
            mode="multiple"
            maxTagCount="responsive"
            allowClear
            showSearch
            style={{ width: "100%" }}
            placeholder={
              addProductType && !FETCHABLE_TYPES[addProductType]
                ? "All Products — no specific products available"
                : "Select products (empty = all)"
            }
            value={addProductIds}
            onChange={(val) => setAddProductIds(val)}
            disabled={!addProductType || !FETCHABLE_TYPES[addProductType]}
            loading={addProductListLoading}
            filterOption={(input, option) =>
              (option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {addProductList.map((item) => (
              <Select.Option key={item.id} value={item.id}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={4}>
          <Button
            type="primary"
            onClick={handleAdd}
            loading={addLoading}
            disabled={!addProductType}
          >
            Add Permission
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default withRouter(TmsSmCounterScanPermissions);
